import {
  Box, Chip, Divider, Tooltip, Typography,
} from '@mui/material'
import {
  Email as EmailIcon,
  AttachFile as AttachFileIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'
import { EmailLog } from '../../lib/api/emailApi'
import { themeColors } from '../../app/layout/theme'
import { EntityModal } from './EntityModal'

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const STATUS_COLOR: Record<string, { bg: string; text: string }> = {
  SENT:   { bg: '#dcfce7', text: '#15803d' },
  DRAFT:  { bg: '#fef9c3', text: '#854d0e' },
  FAILED: { bg: '#fee2e2', text: '#dc2626' },
}

interface Props {
  open: boolean
  onClose: () => void
  email: EmailLog | null
}

export function EmailDetailModal({ open, onClose, email }: Props) {
  if (!email) return null

  const sc = STATUS_COLOR[email.status] ?? STATUS_COLOR.SENT

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title="Email Details"
      icon={<EmailIcon />}
      maxWidth="md"
      actions={
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>
            Created at {formatDate(email.createdAt)}
          </Typography>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        
        {/* Header Info */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '100px 1fr', rowGap: 1.5 }}>
          <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, fontWeight: 600 }}>Subject</Typography>
          <Typography sx={{ fontSize: '14px', color: themeColors.text.primary, fontWeight: 700 }}>
            {email.subject}
          </Typography>

          <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, fontWeight: 600 }}>From</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <PersonIcon sx={{ fontSize: 16, color: themeColors.text.secondary }} />
            <Typography sx={{ fontSize: '13px', color: themeColors.text.primary }}>
              {email.sentByName ?? 'System'}
            </Typography>
          </Box>

          <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, fontWeight: 600 }}>To</Typography>
          <Typography sx={{ fontSize: '13px', color: themeColors.text.primary }}>{email.to.join(', ')}</Typography>

          {email.cc.length > 0 && (
            <>
              <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, fontWeight: 600 }}>CC</Typography>
              <Typography sx={{ fontSize: '13px', color: themeColors.text.primary }}>{email.cc.join(', ')}</Typography>
            </>
          )}

          {email.bcc.length > 0 && (
            <>
              <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, fontWeight: 600 }}>BCC</Typography>
              <Typography sx={{ fontSize: '13px', color: themeColors.text.primary }}>{email.bcc.join(', ')}</Typography>
            </>
          )}

          <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, fontWeight: 600 }}>Status</Typography>
          <Box>
            <Chip
              label={email.status}
              size="small"
              sx={{ fontSize: '11px', height: 22, bgcolor: sc.bg, color: sc.text, fontWeight: 700, border: 'none' }}
            />
          </Box>

          <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, fontWeight: 600 }}>Sent At</Typography>
          <Typography sx={{ fontSize: '13px', color: themeColors.text.primary }}>
            {formatDate(email.sentAt ?? email.createdAt)}
          </Typography>
        </Box>

        <Divider />

        {/* Body Content */}
        <Box>
          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: themeColors.text.secondary, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Message Content
          </Typography>
          {email.status !== 'FAILED' ? (
            <Box
              sx={{
                border: `1px solid ${themeColors.border}`, borderRadius: '8px',
                p: 3, bgcolor: '#fcfcfc', fontSize: '14px', color: themeColors.text.primary,
                lineHeight: 1.6, maxHeight: 400, overflowY: 'auto',
                '& *': { maxWidth: '100%' },
              }}
              dangerouslySetInnerHTML={{ __html: email.htmlBody || '<em>(No content)</em>' }}
            />
          ) : (
            <Box sx={{ py: 2, px: 2, bgcolor: '#fef2f2', borderRadius: '8px', border: '1px solid #fee2e2' }}>
              <Typography sx={{ fontSize: '13px', color: '#dc2626', fontWeight: 500 }}>
                This email failed to send. No message was delivered.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Attachments */}
        {email.attachments.length > 0 && (
          <Box>
            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: themeColors.text.secondary, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Attachments ({email.attachments.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {email.attachments.map((a, i) => (
                <Box
                  key={i}
                  onClick={() => a.url && window.open(a.url, '_blank')}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25,
                    border: `1px solid ${themeColors.border}`, borderRadius: '8px',
                    px: 2, py: 1, bgcolor: '#fff',
                    cursor: a.url ? 'pointer' : 'default',
                    transition: 'all 0.2s',
                    '&:hover': a.url ? { borderColor: themeColors.primary, bgcolor: '#f5f3ff', transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' } : {},
                  }}
                >
                  <AttachFileIcon sx={{ fontSize: 16, color: a.url ? themeColors.primary : '#9ca3af', transform: 'rotate(45deg)' }} />
                  <Box>
                    <Typography sx={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: a.url ? themeColors.primary : '#6b7280',
                      maxWidth: 250,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                    }}>
                      {a.name}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>
                      {formatSize(a.sizeBytes)}
                    </Typography>
                  </Box>
                  {a.url && (
                    <VisibilityIcon sx={{ fontSize: 16, color: themeColors.primary, ml: 1, opacity: 0.6 }} />
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </EntityModal>
  )
}
